export type LanguageCode = 'en' | 'ar';
export const visitorAnalyticsEventNames = [
  'page_view',
  'page_leave',
  'interaction',
  'cta_click',
  'signup_start',
  'signup_complete',
] as const;
export type VisitorAnalyticsEventName = (typeof visitorAnalyticsEventNames)[number];
export type CareerStage = 'student' | 'fresh_graduate' | 'professional' | 'career_switcher';
export type PrimaryGoal =
  'first_cv' | 'improve_cv' | 'improve_linkedin' | 'prepare_interview' | 'specific_job';
export type Seniority = 'intern' | 'entry' | 'mid' | 'senior' | 'lead' | 'manager';

export interface PersonalDetails {
  fullName: string;
  email: string;
  phone: string;
  city: string;
  country: string;
  linkedinUrl: string;
  portfolioUrl: string;
}

export interface TargetDetails {
  role: string;
  industry: string;
  country: string;
  seniority: Seniority | '';
}

export interface EducationEntry {
  id?: string;
  institution: string;
  degree: string;
  field: string;
  startDate: string;
  endDate: string;
  grade: string;
  description: string;
}

export interface ExperienceEntry {
  id?: string;
  company: string;
  jobTitle: string;
  location: string;
  startDate: string;
  endDate: string;
  current: boolean;
  bullets: string[];
}

export interface ProjectEntry {
  id?: string;
  name: string;
  role: string;
  description: string;
  technologies: string[];
  result: string;
}

export interface SkillEntry {
  id?: string;
  name: string;
  category: 'technical' | 'software' | 'soft' | 'tools';
  level?: 'beginner' | 'intermediate' | 'advanced';
}

export interface CertificationEntry {
  id?: string;
  name: string;
  issuer: string;
  issueDate: string;
  credentialUrl: string;
}

export interface LanguageEntry {
  id?: string;
  name: string;
  proficiency: 'basic' | 'conversational' | 'professional' | 'native';
}

export interface AchievementEntry {
  id?: string;
  title: string;
  description: string;
  date: string;
}

export interface CareerProfile {
  careerStage: CareerStage;
  preferredLanguage: LanguageCode;
  primaryGoal: PrimaryGoal;
  personal: PersonalDetails;
  target: TargetDetails;
  summary: string;
  education: EducationEntry[];
  experiences: ExperienceEntry[];
  projects: ProjectEntry[];
  skills: SkillEntry[];
  certifications: CertificationEntry[];
  languages: LanguageEntry[];
  achievements: AchievementEntry[];
}

export const resumeTemplateIds = [
  'ats-classic',
  'modern-professional',
  'fresh-graduate',
  'executive-slate',
  'minimalist',
  'corporate-blue',
  'clean-serif',
  'modern-sidebar',
  'elegant-gray',
  'creative-accent',
  'tech-focus',
  'academic',
  'compact-professional',
  'two-column-pro',
  'graduate-spotlight',
] as const;

export type ResumeTemplate = (typeof resumeTemplateIds)[number];
export type ResumeSection =
  | 'summary'
  | 'experience'
  | 'education'
  | 'projects'
  | 'skills'
  | 'certifications'
  | 'languages'
  | 'achievements';

export interface ResumeDocument {
  id?: string;
  title: string;
  template: ResumeTemplate;
  language: LanguageCode;
  sectionOrder: ResumeSection[];
  hiddenSections: ResumeSection[];
  profile: CareerProfile;
  createdAt?: string;
  updatedAt?: string;
}

export type ScoreSeverity = 'success' | 'info' | 'warning' | 'critical';

export interface ScoreIssue {
  id: string;
  severity: ScoreSeverity;
  category: string;
  title: string;
  explanation: string;
  location: string;
  fix: string;
}

export interface ScoreResult {
  score: number;
  label: 'Needs work' | 'Fair' | 'Good' | 'Strong' | 'Excellent';
  breakdown: Record<string, number>;
  strengths: ScoreIssue[];
  improvements: ScoreIssue[];
}

export interface LinkedInProfileInput {
  headline: string;
  about: string;
  experienceText: string;
  educationText: string;
  skills: string[];
  projectsText: string;
  certificatesText: string;
  pastedProfileText: string;
}

export interface JobInput {
  id?: string;
  title: string;
  company: string;
  description: string;
  language?: LanguageCode;
}

export interface JobMatchResult {
  score: number;
  strongMatches: string[];
  notEvidenced: string[];
  requirementsToHighlight: string[];
  possibleGaps: string[];
  recommendedChanges: string[];
  interviewRiskAreas: string[];
  extractedKeywords: string[];
}

export type InterviewCategory = 'hr' | 'technical' | 'cv_specific' | 'gap' | 'behavioral';

export interface InterviewQuestion {
  question: string;
  category: InterviewCategory;
  difficulty: 'easy' | 'medium' | 'hard';
  whyThisMayBeAsked: string;
  whatTheInterviewerIsLookingFor: string;
  keyPointsToPrepare: string[];
  relatedSkills: string[];
  riskArea?: string;
}

export interface TellMeAboutYourself {
  thirtySeconds: string;
  sixtySeconds: string;
  ninetySeconds: string;
}

export interface InterviewPack {
  id?: string;
  jobTitle: string;
  questions: InterviewQuestion[];
  tellMeAboutYourself: TellMeAboutYourself;
  createdAt?: string;
}

export interface UserSummary {
  id: string;
  email: string;
  fullName: string;
  role: 'user' | 'admin';
  plan: 'free' | 'pro';
  onboardingCompleted: boolean;
  preferredLanguage: LanguageCode;
  mustChangePassword: boolean;
}

export interface PurchaserAccessSummary {
  userId: string;
  email: string;
  fullName: string;
  status: 'active' | 'pending_setup';
  grantedAt: string;
  claimedAt: string | null;
}

export interface PurchaserGrantResult {
  purchaser: PurchaserAccessSummary;
  existingUser: boolean;
  /** Returned only when an administrator must deliver a new one-time setup link. */
  setupToken: string | null;
}

export type IntakeFormStatus = 'open' | 'completed' | 'revoked';

export interface IntakeFormSummary {
  id: string;
  name: string;
  language: LanguageCode;
  status: IntakeFormStatus;
  createdAt: string;
  completedAt: string | null;
  completedUserId: string | null;
}

export interface PublicIntakeForm {
  id: string;
  name: string;
  language: LanguageCode;
  status: IntakeFormStatus;
}

export type WhatsAppLanguage = 'unknown' | LanguageCode;
export type WhatsAppDefaultLanguage = 'auto' | LanguageCode;
export type WhatsAppStage =
  | 'lead'
  | 'payment_pending'
  | 'payment_review'
  | 'payment_approved'
  | 'form_sent'
  | 'form_completed'
  | 'handed_off'
  | 'closed';
export type WhatsAppMessageStatus =
  'received' | 'processing' | 'sent' | 'delivered' | 'read' | 'failed' | 'ignored';

export interface WhatsAppSettings {
  enabled: boolean;
  autoReplyEnabled: boolean;
  sendFormOnApproval: boolean;
  assistantName: string;
  businessDescription: string;
  serviceName: string;
  servicePriceMinor: number;
  currency: string;
  defaultLanguage: WhatsAppDefaultLanguage;
  textModel: string;
  visionModel: string;
  transcriptionModel: string;
  graphApiVersion: string;
  phoneNumberId: string;
  wabaId: string;
  systemPrompt: string;
  knowledgeBase: string;
  paymentInstructionsEn: string;
  paymentInstructionsAr: string;
  formMessageEn: string;
  formMessageAr: string;
  completionMessageEn: string;
  completionMessageAr: string;
  handoffMessageEn: string;
  handoffMessageAr: string;
  maxContextMessages: number;
  mediaRetentionDays: number;
  updatedAt: string;
}

export interface WhatsAppCredentialStatus {
  verifyTokenConfigured: boolean;
  accessTokenConfigured: boolean;
  appSecretConfigured: boolean;
  phoneNumberConfigured: boolean;
  wabaConfigured: boolean;
  r2Configured: boolean;
  webhookUrl: string;
  ready: boolean;
}

export interface WhatsAppOverview {
  totalContacts: number;
  pendingPayments: number;
  activeConversations: number;
  failedMessages: number;
}

export interface WhatsAppConversationSummary {
  waId: string;
  profileName: string;
  customerName: string;
  language: WhatsAppLanguage;
  stage: WhatsAppStage;
  aiPaused: boolean;
  intakeFormId: string | null;
  lastMessageAt: string;
  lastMessageText: string;
  lastMessageDirection: 'inbound' | 'outbound' | 'system' | null;
}

export interface WhatsAppMessageSummary {
  id: string;
  metaMessageId: string | null;
  direction: 'inbound' | 'outbound' | 'system';
  messageType: 'text' | 'audio' | 'image' | 'document' | 'interactive' | 'status' | 'system';
  text: string;
  transcript: string;
  mimeType: string;
  status: WhatsAppMessageStatus;
  errorCode: string;
  createdAt: string;
}

export interface WhatsAppPaymentReview {
  id: string;
  waId: string;
  profileName: string;
  objectKey: string;
  mimeType: string;
  amountMinor: number | null;
  currency: string;
  paymentReference: string;
  recipient: string;
  sender: string;
  transactionAt: string | null;
  confidence: number | null;
  status: 'pending' | 'approved' | 'rejected';
  adminNote: string;
  intakeFormId: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export interface DashboardSummary {
  cvScore: number;
  linkedinScore: number;
  latestJobMatch: number | null;
  interviewReadiness: number;
  profileCompletion: number;
  recentResumes: Array<{ id: string; title: string; updatedAt: string }>;
  recentJobs: Array<{ id: string; title: string; company: string; score: number | null }>;
  recentInterviewPacks: Array<{ id: string; jobTitle: string; createdAt: string }>;
  recommendedNextStep: {
    id: 'profile' | 'cv_create' | 'cv_improve' | 'linkedin' | 'job_match' | 'interview' | 'tailor';
    href: string;
  };
}
